import {
	ActivityLog,
	ApprovalCollectionPayload,
	CreateReviewApproval,
	ResetReviewApproval,
	ReviewApproval,
	ReviewApprovalReminderPayload,
	ReviewApprovalResponsePayload,
	RevisionResponseUpdatePayload,
} from './types';

interface Configuration {
	origin: string;
	getAccessToken?: () => Promise<string>;
}

const configuration: Configuration = {
	origin: `${window.origin}/io/reviewapproval/api/v1`,
};

export function configureReviewApprovalHttp(
	configure: (current: Configuration) => void
): void {
	configure(configuration);
}
async function createRequest(path: string, init: RequestInit = {}): Promise<Request> {
	init.headers = { ...init.headers, 'X-BFF-CSRF': 'true' };
	if (!(path.startsWith('http') || path.startsWith('//')) && configuration.origin) {
		const normalizedBase = configuration.origin.endsWith('/')
			? configuration.origin.slice(0, -1)
			: configuration.origin;
		const normalizedPath = path.startsWith('/') ? path : `/${path}`;
		path = `${normalizedBase}${normalizedPath}`;
	}

	return new Request(path, init);
}

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(errorText || 'Request failed');
	}
	const data = await response.json();
	return data as T;
}

async function handleVoidResponse(response: Response) {
	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(errorText || 'Request failed');
	}
}

export const ReviewApprovalHttp = {
	async postV1CreateApproval(requestBody: CreateReviewApproval): Promise<ReviewApproval> {
		const request = await createRequest('/reviewapprovals', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(requestBody),
		});

		const response = await fetch(request);
		return handleResponse<ReviewApproval>(response);
	},
	async getV1Approval(approvalId: string): Promise<ReviewApproval> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}`,
			{
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
		);

		const response = await fetch(request);
		return handleResponse<ReviewApproval>(response);
	},
	async getV1Approvals(containerRID: string): Promise<ApprovalCollectionPayload> {
		const params = new URLSearchParams();
		params.append('containerRID', containerRID);
		const request = await createRequest(`/reviewapprovals?${params.toString()}`, {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		});
		const response = await fetch(request);
		return handleResponse<ApprovalCollectionPayload>(response);
	},
	/**
	 * Get a list of all activities for a specific approval request
	 * @param approvalId review approval ID
	 * @returns List of activities
	 */
	async getV1ApprovalActivities(approvalId: string): Promise<ActivityLog> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}/activitylog`,
			{
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
		);

		const response = await fetch(request);
		return handleResponse<ActivityLog>(response);
	},
	async putV1UpdateReviewApproval(
		approvalId: string,
		newBody: ReviewApproval
	): Promise<ReviewApproval> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}`,
			{
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(newBody),
			}
		);

		const response = await fetch(request);
		return handleResponse<ReviewApproval>(response);
	},
	async postV1CreateApprovalRevision(
		approvalId: string,
		requestBody: ResetReviewApproval
	): Promise<void> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}/revisions`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(requestBody),
			}
		);

		await fetch(request);
	},
	async postV1ApprovalResponse(
		approvalId: string,
		body: ReviewApprovalResponsePayload
	): Promise<void> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}/responses`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			}
		);

		const response = await fetch(request);
		await handleVoidResponse(response);
	},
	async postV1ApprovalStatus(
		approvalId: string,
		body: RevisionResponseUpdatePayload
	): Promise<void> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}/status`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			}
		);

		await fetch(request);
	},
	async deleteV1Approval(approvalId: string): Promise<void> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(approvalId)}`,
			{
				method: 'DELETE',
				headers: { 'Content-Type': 'application/json' },
			}
		);

		await fetch(request);
	},
	async postV1Reminder(RID: string, body: ReviewApprovalReminderPayload): Promise<void> {
		const request = await createRequest(
			`/reviewapprovals/${encodeURIComponent(RID)}/reminder`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			}
		);
		await fetch(request);
	},
};
